import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarBlank, Users, Hourglass, PencilSimple } from '@phosphor-icons/react';
import { useAuth } from '../hooks/useAuth';
import { useVisitAllocation } from '../hooks/useVisitAllocation';
import WorkerAllocation from '../components/WorkerAllocation';
import '../pages/ProviderDashboard.css';
import './CalendarSchedule.css';

function formatLocalDate(d = new Date()) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const toDateKey = (date) => formatLocalDate(date);

const CalendarSchedule = () => {
  const { activeUserEmail, activeUserRoles, activeEmployeeId, loading: authLoading } = useAuth();
  const todayStr = useMemo(() => formatLocalDate(new Date()), []);
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selectedWorker, setSelectedWorker] = useState('');
  const [schedule, setSchedule] = useState([]);
  const allocation = useVisitAllocation((visitId, assignment) => {
    setSchedule((current) => current.map((item) => String(item.id) === String(visitId)
      ? { ...item, workerId: assignment.workerId, worker: assignment.worker }
      : item));
  });
  const { workers } = allocation;
  const activeRoleHeader = (activeUserRoles || []).join(',');
  const [loading, setLoading] = useState(true);

  // Compute 42 calendar grid days (6 weeks) for the visible month
  const calendarDays = useMemo(() => {
    const firstDay = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1);
    const start = new Date(firstDay);
    start.setDate(1 - firstDay.getDay());
    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(start);
      date.setDate(start.getDate() + index);
      return date;
    });
  }, [visibleMonth]);

  const dateRange = useMemo(() => {
    if (calendarDays.length === 0) return { start: '', end: '' };
    return {
      start: toDateKey(calendarDays[0]),
      end: toDateKey(calendarDays[calendarDays.length - 1]),
    };
  }, [calendarDays]);

  useEffect(() => {
    if (authLoading || !dateRange.start || !dateRange.end) return;

    let isMounted = true;
    const controller = new AbortController();

    const fetchMonthShifts = async () => {
      try {
        setLoading(true);
        const queryParams = new URLSearchParams({
          employeeId: activeEmployeeId || '167',
          start: dateRange.start,
          end: dateRange.end,
        }).toString();

        const res = await fetch(`/api/shifts?${queryParams}`, {
          signal: controller.signal,
          headers: {
            'x-kcs-user-email': activeUserEmail || '',
            'x-kcs-user-roles': activeRoleHeader,
            'x-kcs-provider-employee-id': activeEmployeeId || '167',
          },
        });

        if (!res.ok) {
          throw new Error(`Failed to load calendar shifts: ${res.status}`);
        }

        const data = await res.json();
        const shifts = data.shifts || [];

        if (isMounted) {
          setSchedule(shifts);
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error loading calendar schedule:', err);
          if (isMounted) {
            setSchedule([]);
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchMonthShifts();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [authLoading, activeEmployeeId, dateRange, activeUserEmail, activeRoleHeader]);

  const visibleSchedule = useMemo(() => {
    return schedule.filter((item) => {
      const effectiveWorker = item.worker;
      if (selectedWorker && effectiveWorker !== selectedWorker) return false;
      return true;
    });
  }, [schedule, selectedWorker]);

  const selectedItems = useMemo(() => {
    return visibleSchedule.filter((item) => item.scheduledDate === selectedDate);
  }, [visibleSchedule, selectedDate]);

  const monthLabel = visibleMonth.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });

  const selectDate = (date) => {
    const key = toDateKey(date);
    setSelectedDate(key);
    if (date.getMonth() !== visibleMonth.getMonth()) {
      setVisibleMonth(new Date(date.getFullYear(), date.getMonth(), 1));
    }
  };

  const changeMonth = (offset) => {
    setVisibleMonth((current) => new Date(current.getFullYear(), current.getMonth() + offset, 1));
  };

  const getStatusClass = (status) => {
    const statusMap = {
      'Done': 'status-completed',
      'Completed': 'status-completed',
      'Not done': 'status-amber',
      'Cancelled': 'status-default',
      'Confirmed': 'status-confirmed',
      'Alert': 'status-amber',
      'Due': 'status-due',
      'Overdue': 'status-overdue',
    };
    return statusMap[status] || 'status-default';
  };

  const getStatusDotClass = (status) => {
    const s = String(status || '').toLowerCase();
    if (s === 'done' || s === 'completed') return 'status-dot-done';
    if (s === 'not done' || s === 'cancelled') return 'status-dot-alert';
    if (s.includes('overdue') || s.includes('missed')) return 'status-dot-overdue';
    if (s.includes('alert') || s.includes('late')) return 'status-dot-alert';
    return 'status-dot-due';
  };

  return (
    <div className="provider-dashboard calendar-schedule-page">
      <div className="page-header calendar-page-header">
        <div>
          <h1>Calendar</h1>
        </div>
        <label className="calendar-date-picker">
          <span>Jump to date</span>
          <input
            type="date"
            value={selectedDate}
            onChange={(event) => {
              if (event.target.value) {
                const [y, m, d] = event.target.value.split('-').map(Number);
                selectDate(new Date(y, m - 1, d));
              }
            }}
          />
        </label>
      </div>
      {allocation.workersError && <div role="alert">{allocation.workersError}</div>}

      <div className="calendar-toolbar">
        <div className="calendar-toolbar-title">
          <CalendarBlank size={22} /> <strong>{monthLabel}</strong>
        </div>
        <div className="calendar-toolbar-actions">
          <select
            value={selectedWorker}
            onChange={(event) => setSelectedWorker(event.target.value)}
            aria-label="Filter by worker"
          >
            <option value="">All workers</option>
            {(workers || []).map((worker) => {
              const name = `${worker.firstName} ${worker.lastName}`.trim();
              return <option key={worker.id} value={name}>{name}</option>;
            })}
          </select>
          <button
            type="button"
            onClick={() => {
              const now = new Date();
              setVisibleMonth(new Date(now.getFullYear(), now.getMonth(), 1));
              setSelectedDate(formatLocalDate(now));
            }}
          >
            Today
          </button>
          <button type="button" onClick={() => changeMonth(-1)} aria-label="Previous month">
            Previous
          </button>
          <button type="button" onClick={() => changeMonth(1)} aria-label="Next month">
            Next
          </button>
        </div>
      </div>

      <section className="calendar-layout">
        <div className="calendar-grid" aria-label="Monthly schedule calendar">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} className="calendar-weekday">{day}</div>
          ))}
          {calendarDays.map((date) => {
            const dateKey = toDateKey(date);
            const dayItems = visibleSchedule.filter((item) => item.scheduledDate === dateKey);
            const isCurrentMonth = date.getMonth() === visibleMonth.getMonth();
            const isToday = dateKey === todayStr;

            return (
              <button
                type="button"
                key={dateKey}
                className={`calendar-day ${isCurrentMonth ? '' : 'calendar-day-muted'} ${selectedDate === dateKey ? 'calendar-day-selected' : ''} ${isToday ? 'calendar-day-today' : ''}`}
                onClick={() => selectDate(date)}
              >
                <span className="calendar-day-number">{date.getDate()}</span>
                {dayItems.length > 0 && (
                  <span className="calendar-day-count">
                    {dayItems.length} {dayItems.length === 1 ? 'visit' : 'visits'}
                  </span>
                )}
                <span className="calendar-day-dots">
                  {dayItems.slice(0, 4).map((item, idx) => (
                    <i key={item.id || idx} className={`calendar-dot ${getStatusDotClass(item.status)}`} />
                  ))}
                </span>
              </button>
            );
          })}
        </div>

        <aside className="calendar-agenda">
          <div className="agenda-heading">
            <div>
              <p className="eyebrow">Selected Day</p>
              <h2>
                {(() => {
                  const [y, m, d] = selectedDate.split('-').map(Number);
                  return new Date(y, m - 1, d).toLocaleDateString('en-AU', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                  });
                })()}
              </h2>
            </div>
            <span>{selectedItems.length}</span>
          </div>

          {loading ? (
            <p className="agenda-empty">Loading visits...</p>
          ) : selectedItems.length === 0 ? (
            <p className="agenda-empty">No visits scheduled for this day.</p>
          ) : (
            selectedItems.map((item) => {
              return (
                <article key={item.id} className="agenda-item">
                  <div className="agenda-item-top">
                    <strong>{item.time}</strong>
                    <span className={`status-badge ${getStatusClass(item.status)}`}>
                      {item.status}
                    </span>
                  </div>
                  <h3>{item.customer}</h3>
                  <p>{item.serviceType || item.service}</p>
                  <div className="agenda-meta">
                    {item.duration && (
                      <span>
                        <Hourglass size={16} /> {item.duration}
                      </span>
                    )}
                    <div>
                      <Users size={16} />
                      <WorkerAllocation item={item} {...allocation} saving={allocation.savingAssignments[item.id]} error={allocation.assignmentErrors[item.id]} />
                    </div>
                  </div>
                  <div className="card-actions" style={{ marginTop: '0.6rem' }}>
                    <Link
                      to={`/provider/services/${item.id}/edit-job`}
                      className="action-button btn-icon"
                      title="Edit job"
                    >
                      <PencilSimple size={20} weight="regular" />
                    </Link>
                  </div>
                </article>
              );
            })
          )}
        </aside>
      </section>
    </div>
  );
};

export default CalendarSchedule;
